import React, { useEffect, useRef, useState } from "react";
import { api, getReadableError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CloudArrowUp, X, CheckCircle, Camera, Image as ImageIcon } from "@phosphor-icons/react";
import CameraCapture from "@/components/CameraCapture";

export const DOC_TYPE_LABELS = {
  cedula: "Cédula",
  credencial_electoral: "Credencial Electoral",
  pasaporte: "Pasaporte",
  residencia: "Residencia",
  licencia: "Licencia",
  otro: "Otro",
};

const EMPTY = {
  doc_type: "cedula",
  doc_label: "",
  doc_number: "",
  notes: "",
};

export default function DocumentFormDialog({
  open,
  onOpenChange,
  clientId,
  doc,
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(
        doc
          ? {
              doc_type: doc.doc_type || "cedula",
              doc_label: doc.doc_label || "",
              doc_number: doc.doc_number || "",
              notes: doc.notes || "",
            }
          : EMPTY
      );
      setFrontFile(null);
      setBackFile(null);
      setFrontPreview(null);
      setBackPreview(null);
    }
  }, [open, doc]);

  // Local image previews
  useEffect(() => {
    if (!frontFile) {
      setFrontPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(frontFile);
    setFrontPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [frontFile]);

  useEffect(() => {
    if (!backFile) {
      setBackPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(backFile);
    setBackPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [backFile]);

  const isEdit = Boolean(doc?.id);

  const uploadSide = async (docId, side, file) => {
    const fd = new FormData();
    fd.append("side", side);
    fd.append("file", file);
    const { data } = await api.post(`/documents/${docId}/upload`, fd);
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const r = await api.patch(`/documents/${doc.id}`, form);
        saved = r.data;
      } else {
        const r = await api.post(`/clients/${clientId}/documents`, form);
        saved = r.data;
      }

      // Sequentially upload selected images
      if (frontFile) {
        saved = await uploadSide(saved.id, "front", frontFile);
      }
      if (backFile) {
        saved = await uploadSide(saved.id, "back", backFile);
      }

      onSaved && onSaved(saved);
      toast.success(isEdit ? "Documento actualizado" : "Documento guardado");
    } catch (err) {
      toast.error(getReadableError(err));
    } finally {
      setSaving(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border-slate-900 max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="document-form-dialog"
      >
        <DialogHeader>
          <div className="label-eyebrow text-[#002FA7]">
            {isEdit ? "·Editar documento" : "·Nuevo documento"}
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? DOC_TYPE_LABELS[form.doc_type] : "Agregar documento"}
          </DialogTitle>
          <DialogDescription>
            Define el tipo y sube las fotos. Puedes reemplazarlas luego desde la ficha.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="label-eyebrow">Tipo</Label>
            <div className="mt-1.5">
              <Select
                value={form.doc_type}
                onValueChange={(v) => setForm((f) => ({ ...f, doc_type: v }))}
              >
                <SelectTrigger
                  className="rounded-none h-11 border-slate-300 focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-0"
                  data-testid="document-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-slate-900">
                  {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} className="rounded-none">
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Etiqueta (opcional)</Label>
              <Input
                value={form.doc_label}
                onChange={update("doc_label")}
                className="mt-1.5 rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0"
                placeholder="Ej: Pasaporte UE"
                data-testid="document-label-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Número</Label>
              <Input
                value={form.doc_number}
                onChange={update("doc_number")}
                className="mt-1.5 rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0 font-mono-tab"
                placeholder="00000000"
                data-testid="document-number-input"
              />
            </div>
          </div>

          {/* Upload zones */}
          <div>
            <Label className="label-eyebrow">
              Fotos del documento {!isEdit && <span className="text-slate-400 normal-case tracking-normal font-normal">(opcionales — puedes subirlas luego)</span>}
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <FilePickZone
                label="Frontal"
                file={frontFile}
                preview={frontPreview}
                existingPath={doc?.front_path}
                onPick={setFrontFile}
                testId="dialog-pick-front"
              />
              <FilePickZone
                label="Trasera"
                file={backFile}
                preview={backPreview}
                existingPath={doc?.back_path}
                onPick={setBackFile}
                testId="dialog-pick-back"
              />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Notas</Label>
            <Textarea
              value={form.notes}
              onChange={update("notes")}
              className="mt-1.5 rounded-none border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7] focus-visible:ring-offset-0 min-h-[72px]"
              placeholder="Vencimiento, observaciones..."
              data-testid="document-notes-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-none border-slate-300"
              data-testid="document-form-cancel-button"
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-none bg-[#002FA7] hover:bg-[#00217A] text-white font-bold uppercase tracking-wider btn-swap"
              data-testid="document-form-submit-button"
            >
              {saving
                ? "Guardando..."
                : isEdit
                ? "Guardar"
                : frontFile || backFile
                ? "Crear y subir"
                : "Crear documento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FilePickZone({ label, file, preview, existingPath, onPick, testId }) {
  const galleryRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const openGallery = () => galleryRef.current?.click();
  const openCamera = () => setCameraOpen(true);

  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Selecciona una imagen");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Máximo 15MB");
      return;
    }
    onPick(f);
  };

  const handleCameraCapture = (file) => {
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Máximo 15MB");
      return;
    }
    onPick(file);
  };

  const clear = (e) => {
    e.stopPropagation();
    onPick(null);
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const hasPreview = Boolean(preview);
  const hasExisting = Boolean(existingPath) && !file;

  return (
    <div
      className="drop-zone relative aspect-[16/10] flex items-center justify-center overflow-hidden"
      data-has-image={hasPreview}
      data-testid={testId}
    >
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
        data-testid={`${testId}-input`}
      />
      <CameraCapture
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        label={label}
        onCapture={handleCameraCapture}
      />
      {hasPreview ? (
        <>
          <img
            src={preview}
            alt={label}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-start justify-between p-2">
            <span className="label-eyebrow bg-[#6B21A8] text-white px-1.5 py-0.5 inline-flex items-center gap-1">
              <CheckCircle size={12} weight="fill" /> {label}
            </span>
            <button
              type="button"
              onClick={clear}
              className="bg-white text-slate-900 p-1 hover:bg-red-600 hover:text-white transition-colors"
              data-testid={`${testId}-remove`}
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        </>
      ) : (
        <div className="text-center px-3 w-full">
          <CloudArrowUp size={24} weight="bold" className="mx-auto text-slate-400" />
          <div className="label-eyebrow mt-1.5">{label}</div>
          <div className="text-[11px] text-slate-500 mt-1 mb-2">
            {hasExisting ? "Reemplazar (opcional)" : "Subir imagen"}
          </div>
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={openCamera}
              className="inline-flex items-center gap-1 px-2 py-1 bg-[#6B21A8] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#581C87] transition-colors"
              data-testid={`${testId}-camera-btn`}
            >
              <Camera size={11} weight="bold" /> Cámara
            </button>
            <button
              type="button"
              onClick={openGallery}
              className="inline-flex items-center gap-1 px-2 py-1 border border-slate-700 text-slate-800 text-[10px] font-bold uppercase tracking-wider hover:bg-slate-900 hover:text-white transition-colors"
              data-testid={`${testId}-gallery-btn`}
            >
              <ImageIcon size={11} weight="bold" /> Galería
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
